/* eslint-disable max-len */
/* eslint-disable no-bitwise */
import { ok, err, DeviceError, DeviceResult, transportError, protocolError, verificationError } from './device-error';
import { ScheduleModbusCommand } from './modbus.scheduler';
import {
  CommandPriority,
  DataType,
  InverterData,
  InverterRegister,
  RequestInfo,
  ModbusCommandOps,
  ModbusFunctionType,
  MultiRequest,
  RegisterTable,
  ModbusLinkOptions,
} from './inverter.model';
import { crc16, getHoldingRegistersValueByTable, getMinRegisterPages, printUint8ArrayAsHex } from './modbus.utils';
import { ModbusError, ModbusErrorCode, MODBUS_EXCEPTION_CODE_MAP } from './modbus.multi-request';

/* eslint-disable no-bitwise */
/**
 * Estimates the expected response size (in bytes) for a single Modbus request frame.
 */
export function estimateModbusResponseSize(cmd: Uint8Array): number {
  const fc = cmd[1];
  if (fc === 0x03 || fc === 0x04) {
    const quantity = (cmd[4] << 8) | cmd[5];
    return 5 + quantity * 2;
  }
  if (fc === 0x06) return 8;
  if (fc === 0x10) return 8;
  if (fc === 0x13) return 8;
  return cmd.length;
}

/**
 * Splits a concatenated multi-query buffer into individual Modbus request frames.
 * Each frame is a complete Modbus RTU request: [addr][FC][...payload][crcLo][crcHi].
 *
 * Frame sizes (request side):
 *   FC 0x03/0x04 (read):         8 bytes
 *   FC 0x06     (write single):  8 bytes
 *   FC 0x10     (write multi):   9 + byteCount bytes
 *   FC 0x13     (custom V2):     8 + qty*2 bytes
 */
export function splitModbusFrames(buffer: Uint8Array): Uint8Array[] {
  const frames: Uint8Array[] = [];
  let offset = 0;

  while (offset < buffer.length) {
    if (offset + 2 > buffer.length) break;

    const fc = buffer[offset + 1];
    let frameLen: number;

    if (fc === 0x03 || fc === 0x04 || fc === 0x06) {
      frameLen = 8;
    } else if (fc === 0x10) {
      if (offset + 7 > buffer.length) break;
      const byteCount = buffer[offset + 6];
      frameLen = 9 + byteCount;
    } else if (fc === 0x13) {
      if (offset + 6 > buffer.length) break;
      const quantity = (buffer[offset + 4] << 8) | buffer[offset + 5];
      frameLen = 8 + quantity * 2;
    } else {
      // Unknown FC — consume remaining bytes as single frame
      frameLen = buffer.length - offset;
    }

    if (offset + frameLen > buffer.length) break;
    frames.push(buffer.slice(offset, offset + frameLen));
    offset += frameLen;
  }

  return frames;
}

const MODBUS_WRITE_DEBUG = { ENABLED: true, LOG_PERFORMANCE: true, LOG_DATA: true };
const MODBUS_LINK_DEBUG = { ENABLED: true };
const mlLog = (deviceId: string, msg: string) => MODBUS_LINK_DEBUG.ENABLED && console.log(`[ModbusLink:${deviceId}] ${msg}`);
const _s = (v: unknown) => v !== undefined && v !== null && typeof v === 'object' ? JSON.stringify(v) : (v ?? '');
const modbusWriteLog = {
  info: (msg: string, data?: unknown) => MODBUS_WRITE_DEBUG.ENABLED && console.log(`[ModbusWrite] ${msg}`, _s(data)),
  data: (msg: string, data?: unknown) => MODBUS_WRITE_DEBUG.ENABLED && MODBUS_WRITE_DEBUG.LOG_DATA && console.log(`[ModbusWrite] DATA: ${msg}`, _s(data)),
  startTimer: (label: string) => {
    const timer = { label, start: performance.now() };
    if (MODBUS_WRITE_DEBUG.ENABLED && MODBUS_WRITE_DEBUG.LOG_PERFORMANCE) console.log(`[ModbusWrite] START: ${label}`);
    return timer;
  },
  endTimer: (timer: { label: string; start: number }, details?: Record<string, unknown>) => {
    const duration = performance.now() - timer.start;
    if (MODBUS_WRITE_DEBUG.ENABLED && MODBUS_WRITE_DEBUG.LOG_PERFORMANCE) {
      console.log(`[ModbusWrite] END: ${timer.label} ${duration.toFixed(2)}ms`, _s(details));
      if (duration > 5000) console.warn(`[ModbusWrite] Slow operation: ${timer.label} took ${duration.toFixed(2)}ms`);
    }
    return duration;
  },
};

/** Manages Modbus communications. */
export class ModbusLink {
  private content: Partial<InverterData> = {};
  private _lastComposedSnapshot: Partial<InverterData> = {};
  private _validity: Partial<{ [key: string]: boolean }> = {};
  private registerReadCache: Map<number, Uint8Array> = new Map();
  /** Accumulated composer functions across all executeMixedRequests calls.
   *  Cleared on reset()/destroy(). Applied on every snapshot creation. */
  private _registeredComposers = new Map<string, (data: Partial<InverterData>) => Partial<InverterData>>();

  private readonly MAX_REGISTER_PER_PAGE = 16;
  private MAX_TIMEOUT_IN_MS = 2500;

  private readonly schedule: ScheduleModbusCommand;
  readonly options: ModbusLinkOptions;

  get data(): Partial<InverterData> {
    return this.content;
  }

  /**
   * Last composed snapshot: raw data + global composer transforms (display-ready).
   * Overwritten on every executeMixedRequests call. After multiplexed reads
   * (battery/BDU/BMS/PCU), this contains the LAST selected unit's data only —
   * use the service's return value for per-unit composed data instead.
   */
  get composedData(): Partial<InverterData> {
    return this._lastComposedSnapshot;
  }

  get validity(): Partial<{ [key: string]: boolean }> {
    return this._validity;
  }

  constructor(options: ModbusLinkOptions) {
    this.options = options;
    this.options.maxTimeoutInMs ??= this.MAX_TIMEOUT_IN_MS;
    this.options.maxRegisterPerPage ??= this.MAX_REGISTER_PER_PAGE;

    if (options.deviceId) {
      this.schedule = ScheduleModbusCommand.getInstanceForDevice(
        options.deviceId,
        options.delayBetweenCommandsInMs,
      );
    } else {
      this.schedule = ScheduleModbusCommand.getInstance(options.delayBetweenCommandsInMs);
    }

    mlLog(this?.options?.deviceId ?? '', `Created for deviceId="${options.deviceId || '(default)'}", modbusAddr=${options.modbusAddress}, timeout=${options.maxTimeoutInMs}ms`);
    this.readHoldingRegisters = this.readHoldingRegisters.bind(this);
  }

  /**
   * @deprecated Use instance method `reset()` instead, which targets only this device's scheduler.
   * Kept for retrocompatibility — resets only the default shared scheduler.
   */
  static resetScheduler(): void {
    mlLog('', 'static resetScheduler() called (default only)');
    ScheduleModbusCommand.getInstance().reset();
  }

  /** Resets scheduler state (buffer, semaphore) and clears cached data. Keeps the scheduler in the registry. */
  reset() {
    mlLog(this?.options?.deviceId ?? '', `reset() for deviceId="${this.options.deviceId || '(default)'}"`);
    this.schedule.resetState();

    this.content = {};
    this._lastComposedSnapshot = {};
    this._validity = {};
    this.registerReadCache.clear();
    this._registeredComposers.clear();
  }

  /** Fully destroys the scheduler instance, removing it from the registry. Use in close(). */
  destroy() {
    if (this.options.deviceId) {
      mlLog(this?.options?.deviceId ?? '', `destroy() for deviceId="${this.options.deviceId}"`);
      ScheduleModbusCommand.resetForDevice(this.options.deviceId);
    } else {
      mlLog('', 'destroy() for default scheduler');
      ScheduleModbusCommand.getInstance().reset();
    }

    this.content = {};
    this._lastComposedSnapshot = {};
    this._validity = {};
    this.registerReadCache.clear();
    this._registeredComposers.clear();
  }

  /**
   * Checks whether all registers in `table` are available in cache.
   *
   * Two-level validation:
   * 1. Raw bytes — every register address must exist in `registerReadCache`.
   * 2. Decoded value — the key must exist in `this.content` (raw), protecting
   *    from page-overlap where bytes were cached but the key wasn't decoded.
   */
  hasAllRegistersInCache(table: Partial<RegisterTable>): boolean {
    for (const [key, reg] of Object.entries(table)) {
      if (!reg || reg.isMask) continue;
      const qty = reg.quantity ?? 1;
      for (let i = 0; i < qty; i++) {
        if (!this.registerReadCache.has(reg.address + i)) return false;
      }
      if (!(key in this.content)) return false;
    }
    return true;
  }

  async executeRawOperation(request: Uint8Array): Promise<Uint8Array> {
    const [response] = await this.processRawRequests([request]);
    return response;
  }

  async executeBulkRawOperation(requests: Uint8Array[]): Promise<Uint8Array[]> {
    return this.processRawRequests(requests);
  }

  private async processRawRequests(requests: Uint8Array[]): Promise<Uint8Array[]> {
    const commands = requests.map((request) =>
      this.schedule.addConcurrentRequest({
        concurrentQuery: {
          count: 1,
          encodeMultiQuery: ([requests]: Uint8Array[]) => requests,
          decodeMultiQuery: (multiCmd: Uint8Array) => [multiCmd],
        },
        createdAt: new Date(),
        handleFatalError: this.options.handleFatalError,
        interactionHandler: (multiCmd: Uint8Array) => this.options.executeCmd(multiCmd),
        inverterVersion: this.options.inverterVersion,
        maxTimeoutInMs: this.options.maxTimeoutInMs,
        retryPolicy: this.options.retryPolicy,
        priority: CommandPriority.High,
        requestsRaw: [request],
      })
    );
    const results = await Promise.all(commands);
    return results.map((r) => r[0]);
  }

  async executeMixedRequests(requests: MultiRequest[]): Promise<MultiRequest[]> {
    let concurrencyPromises: Array<Promise<Uint8Array[]>> = [];
    const allRequestsInfo: RequestInfo[] = [];
    const group = `default_${new Date().getTime()}`;

    const mixedSummary = requests.map((r, i) => `[${i}]${r.mode}${r.mode === 'read' ? `(${Object.keys(r.table || {}).length} regs)` : `(${(r as any).key})`}`).join(', ');
    modbusWriteLog.info(`executeMixedRequests: ${requests.length} request(s) — ${mixedSummary}, group=${group}`);

    for (let i = 0; i < requests.length; i++) {
      requests[i].results = requests[i].results || {};
      const req = requests[i];

      if (req.mode === 'read') {
        this.buildReadSubRequests(req, i, concurrencyPromises, allRequestsInfo, group);
      } else {
        this.buildWriteSubRequests(req, i, concurrencyPromises, allRequestsInfo, group);
      }
    }

    modbusWriteLog.info(`executeMixedRequests: built ${concurrencyPromises.length} concurrent promise(s) from ${allRequestsInfo.length} sub-request(s)`);

    // Trigger immediate flush — avoids the 500ms buffer timer delay
    this.schedule.requestFlush();

    const updateProgress = requests.find(r => r.options?.updateProgress)?.options?.updateProgress;
    if (updateProgress && concurrencyPromises.length > 0) {
      let completed = 0;
      const total = concurrencyPromises.length;
      concurrencyPromises = concurrencyPromises.map(p =>
        p.then(
          result => {
            completed++;
            updateProgress(Math.round((completed / total) * 100));
            return result;
          },
          error => {
            completed++;
            updateProgress(Math.round((completed / total) * 100));
            throw error;
          },
        )
      );
    }

    // Settle all promises (es2019-compatible alternative to Promise.allSettled)
    type Settled = { ok: true; value: Uint8Array[] } | { ok: false; error: any };
    const settled: Settled[] = await Promise.all(
      concurrencyPromises.map(p => p.then(
        (value): Settled => ({ ok: true, value }),
        (error): Settled => ({ ok: false, error }),
      ))
    );

    const failedCount = settled.filter(r => !r.ok).length;
    if (failedCount === settled.length && failedCount > 0) {
      const firstFailed = settled.find(r => !r.ok) as { ok: false; error: any };
      if (firstFailed.error instanceof ModbusError) {
        mlLog(this.options.deviceId || '(default)', `executeMixedRequests total failure: ModbusError code=${ModbusErrorCode[firstFailed.error.code]}(${firstFailed.error.code})`);
        throw firstFailed.error;
      }
      throw new Error('Generic error on readTableRegisterByMultipleRequest');
    }

    if (failedCount > 0) {
      mlLog(this.options.deviceId || '(default)', `executeMixedRequests partial failure: ${settled.length - failedCount}/${settled.length} succeeded`);
    }

    const results: (Uint8Array[] | null)[] = settled.map(r => r.ok ? r.value : null);

    this._lastComposedSnapshot = this.decodeAndMergeRequests(allRequestsInfo, results, requests);

    for (let i = 0; i < requests.length; i++) {
      const req = requests[i];
      if (req.mode === 'read') {
        const table = req.table;
        if (table && req.options?.useValidityMask) {
          this.validateRegisterTableByMask(req.options.useValidityMask, table);
        }
        if (table) {
          this.applyContentToCustomRegisterStates(table, this.content);
        }
      }
    }
    return requests;
  }

  private buildReadSubRequests(
    req: Extract<MultiRequest, { mode: 'read' }>,
    orderIndex: number,
    concurrencyPromises: Array<Promise<Uint8Array[]>>,
    allRequestsInfo: RequestInfo[],
    group: string
  ): void {
    const readFn = req.options?.customReadFn
      ? this.readCustomFnRegisters(req.options.customReadFn)
      : this.readHoldingRegisters;

    const maxRegisterPerPage = req.options?.maxRegisterPerPage || this.MAX_REGISTER_PER_PAGE;
    const optimize = req.options?.optimize ?? true;
    const pages = getMinRegisterPages(req.table, maxRegisterPerPage, optimize);

    modbusWriteLog.data(`buildReadSubRequests[${orderIndex}]: ${Object.keys(req.table || {}).length} table regs → ${pages.length} page(s), maxPerPage=${maxRegisterPerPage}, optimize=${optimize}`);

    for (const page of pages) {
      const address = page.address;
      const quantity = page.quantity;
      const modbusRequest = readFn(address, quantity);
      modbusWriteLog.data(`  page @0x${address.toString(16).toUpperCase().padStart(4, '0')}: qty=${quantity}, cmd=${modbusRequest.length}B, estResp=${5 + quantity * 2}B`);

      const overhead = 5;
      const estimatedResponseSize = overhead + quantity * 2;

      concurrencyPromises.push(
        this.schedule.addConcurrentRequest({
          concurrentQuery: req.options?.concurrentQuery || this.options.concurrentQuery,
          createdAt: new Date(),
          estimatedResponseSize,
          group,
          handleFatalError: req.options?.handleFatalError || this.options.handleFatalError,
          interactionHandler: (multiCmd: Uint8Array) => this.options.executeCmd(multiCmd),
          inverterVersion: this.options.inverterVersion,
          maxTimeoutInMs: this.options.maxTimeoutInMs,
          retryPolicy: this.options.retryPolicy,
          optional: req.options?.optional,
          orderIndex,
          priority: CommandPriority.Low,
          requestsRaw: [modbusRequest],
        })
      );

      allRequestsInfo.push({
        address,
        composer: req.composer,
        indexInPromises: concurrencyPromises.length - 1,
        modbusRequest,
        mode: 'read',
        options: req.options,
        orderIndex,
        quantity,
        table: req.table,
      });
    }
  }

  private buildWriteSubRequests(
    req: Extract<MultiRequest, { mode: 'write' }>,
    orderIndex: number,
    concurrencyPromises: Array<Promise<Uint8Array[]>>,
    allRequestsInfo: RequestInfo[],
    group: string
  ): void {
    const element = req.table[req.key];
    if (!element) {
      throw new Error(`Key "${req.key}" not found in table for request #${orderIndex}.`);
    }

    const data = this.buildRegisterValue(req.value, element);
    const modbusRequest = this.writeHoldingRegisters(
      element.address,
      data,
      element.quantity,
      req.options?.customWriteFn
    );

    modbusWriteLog.data(`buildWriteSubRequests[${orderIndex}]: key=${String(req.key)}, addr=0x${element.address.toString(16).toUpperCase().padStart(4, '0')}, value=${req.value}, qty=${element.quantity}, cmd=${modbusRequest.length}B`);

    const estimatedResponseSize = 8;
    concurrencyPromises.push(
      this.schedule.addConcurrentRequest({
        concurrentQuery: req.options?.concurrentQuery || this.options.concurrentQuery,
        createdAt: new Date(),
        estimatedResponseSize,
        group,
        handleFatalError: req.options?.handleFatalError || this.options.handleFatalError,
        interactionHandler: (multiCmd: Uint8Array) => this.options.executeCmd(multiCmd),
        inverterVersion: this.options.inverterVersion,
        maxTimeoutInMs: this.options.maxTimeoutInMs,
        retryPolicy: this.options.retryPolicy,
        optional: req.options?.optional,
        orderIndex,
        priority: CommandPriority.High,
        requestsRaw: [modbusRequest],
      })
    );

    allRequestsInfo.push({
      indexInPromises: concurrencyPromises.length - 1,
      orderIndex,
      mode: 'write',
      modbusRequest,
      options: req.options,
    });
  }

  private decodeAndMergeRequests(
    allRequestsInfo: RequestInfo[],
    results: (Uint8Array[] | null)[],
    requests: MultiRequest[]
  ): Partial<InverterData> {
    for (const info of allRequestsInfo) {
      const subResponses = results[info.indexInPromises];
      if (!subResponses) continue;
      const response = subResponses[0];
      const req = requests[info.orderIndex];

      const requestHex = printUint8ArrayAsHex(info.modbusRequest);
      const responseHex = printUint8ArrayAsHex(response);
      req.results = req.results || {};
      req.results[requestHex] = responseHex;

      if (info.mode === 'write') {
        // Invalidate only the written register addresses
        const writeAddr = (info.modbusRequest[2] << 8) | info.modbusRequest[3];
        const writeQty = (info.modbusRequest[4] << 8) | info.modbusRequest[5];
        for (let wi = 0; wi < writeQty; wi++) {
          this.registerReadCache.delete(writeAddr + wi);
        }
        modbusWriteLog.info(`cache: invalidated ${writeQty} reg(s) @0x${writeAddr.toString(16).toUpperCase().padStart(4, '0')}`);
      }

      if (info.mode === 'read' && info.table && info.address != null && info.quantity != null) {
        const subData = getHoldingRegistersValueByTable(
          info.address,
          info.quantity,
          response,
          info.table
        );

        const decodedKeys = Object.keys(subData);
        modbusWriteLog.data(`decodeAndMerge[${info.orderIndex}] READ @0x${info.address.toString(16).toUpperCase().padStart(4, '0')}: decoded ${decodedKeys.length} key(s) — ${decodedKeys.join(', ')}`);

        this.content = { ...this.content, ...subData };

        // Cache raw register bytes for preliminary-read optimization
        const dataOffset = 3; // response: [deviceAddr, FC, byteCount, ...data, CRC_lo, CRC_hi]
        for (let ri = 0; ri < info.quantity && (dataOffset + ri * 2 + 1) < response.length - 2; ri++) {
          this.registerReadCache.set(info.address + ri,
            new Uint8Array([response[dataOffset + ri * 2], response[dataOffset + ri * 2 + 1]]),
          );
        }

        // Note: prefix option is intentionally ignored here. Multiplexed reads
        // (battery, BDU, BMS, PCU) are handled by the service layer which captures
        // raw snapshots and composes locally — no prefixed keys in this.content.
      }
    }

    // Accumulate composers: register new ones from the current requests.
    // This ensures that composers from earlier calls (e.g. ChatBot read) survive
    // across subsequent calls (e.g. getBatteryDetails) that don't pass composers.
    // Cleared on reset()/destroy().
    for (const request of requests) {
      if (request.mode === 'read' && request.composer) {
        for (const [k, fn] of Object.entries(request.composer)) {
          if (fn) {
            this._registeredComposers.set(k, fn);
          }
        }
      }
    }

    // Apply ALL accumulated composers on a COPY — this.content stays raw.
    // The result is stored in _lastComposedSnapshot (via the caller).
    // For multiplexed reads (retrieve*), the service layer snapshots raw data
    // (channel.command.data) and composes locally — unaffected by this.
    let composedSnapshot = { ...this.content };
    for (const [, fn] of this._registeredComposers) {
      composedSnapshot = fn({ ...composedSnapshot });
    }
    return composedSnapshot;
  }

  private applyContentToCustomRegisterStates(table: Partial<RegisterTable>, content: Partial<InverterData>): void {
    Object.values(table).forEach((element) => {
      if (!element?.custom) return;
      if (element.custom.isEnabledFn) {
        element.custom.isEnabled = element.custom.isEnabledFn(content);
      }
      if (element.custom.bitFunctions) {
        element.custom.bitFunctions.forEach((bitFn) => {
          if (bitFn.isEnabledFn) {
            bitFn.isEnabled = bitFn.isEnabledFn(content);
          }
        });
      }
      if (element.custom.multiBitFunctions) {
        element.custom.multiBitFunctions.forEach((multiBitFn) => {
          if (multiBitFn.selectionFn) {
            multiBitFn.selection = multiBitFn.selectionFn(content);
          }
        });
      }
    });
  }

  async executeWriteMultipleHoldingRegisters(
    values: { key: keyof RegisterTable; value: string | number }[],
    table: Partial<RegisterTable>,
    options?: Partial<ModbusCommandOps>
  ): Promise<void> {
    const defaultOptions: ModbusCommandOps = {
      optimize: true,
      maxRegisterPerPage: 125,
    };
    options = { ...defaultOptions, ...options };

    const filteredTable: Partial<RegisterTable> = {};
    for (const [k, reg] of Object.entries(table)) {
      if (!reg?.isMask) {
        filteredTable[k] = reg;
      }
    }

    // Expand write range to full aligned blocks (e.g. 64-register safety files)
    if (options.writeBlockSize) {
      const blockSize = options.writeBlockSize;
      const addrs = Object.values(filteredTable)
        .filter((r): r is InverterRegister => !!r)
        .map(r => r.address);
      const minAddr = Math.min(...addrs);
      const blockStart = minAddr - (minAddr % blockSize);
      const blockEnd = blockStart + blockSize - 1;

      if (!addrs.includes(blockStart)) {
        filteredTable['__blockStart'] = {
          address: blockStart, name: '__blockStart',
          dataType: DataType.U16, quantity: 1, accuracy: 1,
        } as InverterRegister;
      }
      if (!addrs.includes(blockEnd)) {
        filteredTable['__blockEnd'] = {
          address: blockEnd, name: '__blockEnd',
          dataType: DataType.U16, quantity: 1, accuracy: 1,
        } as InverterRegister;
      }

      options.maxRegisterPerPage = Math.max(
        options.maxRegisterPerPage || 125,
        blockSize + 32,
      );
      modbusWriteLog.info(
        `writeBlockSize=${blockSize}: expanded to 0x${blockStart.toString(16).toUpperCase().padStart(4, '0')}-0x${blockEnd.toString(16).toUpperCase().padStart(4, '0')}`,
      );
    }

    const registers = values
      .map((v) => filteredTable[v.key])
      .filter((reg): reg is InverterRegister => !!reg);

    if (registers.length !== values.length) {
      throw new Error(`Some keys not found in table or are marked isMask=true`);
    }

    const maxRegisterPerPage = options.maxRegisterPerPage || this.MAX_REGISTER_PER_PAGE;
    const commands = getMinRegisterPages(filteredTable, maxRegisterPerPage, options.optimize);

    const extendedValues = values.map((val, index) => {
      const element = registers[index];
      const data = this.buildRegisterValue(val.value, element);
      return { ...val, element, data };
    });

    const updateProgress = options.updateProgress;
    const readOptions = updateProgress
      ? { ...options, updateProgress: (pct: number) => updateProgress(Math.round(pct * 0.5)) }
      : options;

    const writeOverallTimer = modbusWriteLog.startTimer(`writeMultipleHoldingRegisters (${values.length} values, ${commands.length} pages)`);

    // Collect all register addresses needed for write pages (including gap registers)
    const allNeededAddresses = new Set<number>();
    for (const cmd of commands) {
      for (let i = 0; i < cmd.quantity; i++) {
        allNeededAddresses.add(cmd.address + i);
      }
    }

    // Check if all needed registers are cached (invalidated on write, no TTL)
    const allCached = [...allNeededAddresses].every(addr => this.registerReadCache.has(addr));

    let registerResponse: { [addr: number]: Uint8Array };

    if (allCached) {
      modbusWriteLog.info(
        `Preliminary READ SKIPPED — cache hit (${allNeededAddresses.size} regs)`,
      );
      registerResponse = {};
      for (const addr of allNeededAddresses) {
        registerResponse[addr] = this.registerReadCache.get(addr)!;
      }
      updateProgress?.(50);
    } else {
      const prelimTimer = modbusWriteLog.startTimer(
        `Preliminary READ (${Object.keys(filteredTable).length} registers in table)`,
      );

      const [readResult] = await this.executeMixedRequests([
        {
          mode: 'read',
          table: filteredTable,
          options: readOptions,
        },
      ]);

      modbusWriteLog.endTimer(prelimTimer);

      const requestResponse = readResult.results || {};
      registerResponse = Object.entries(requestResponse).reduce(
        (acc: { [addr: number]: Uint8Array }, [reqHex, respHex]) => {
          const reqParts = reqHex.split(' ');
          const startAddress = parseInt(reqParts[2] + reqParts[3], 16);
          const quantity = parseInt(reqParts[4] + reqParts[5], 16);

          const respParts = respHex.split(' ');
          const dataBytes = respParts.slice(3, -2).map((hex) => parseInt(hex, 16));

          for (let i = 0; i < quantity; i++) {
            acc[startAddress + i] = new Uint8Array([
              dataBytes[i * 2],
              dataBytes[i * 2 + 1],
            ]);
          }
          return acc;
        },
        {}
      );
    }

    if (options.useValidityMask) {
      this.validateRegisterTableByMask(options.useValidityMask, table);
    }

    for (let cmdIdx = 0; cmdIdx < commands.length; cmdIdx++) {
      const pageTimer = modbusWriteLog.startTimer(`WRITE page ${cmdIdx + 1}/${commands.length} @0x${commands[cmdIdx].address.toString(16).toUpperCase().padStart(4, '0')}`);
      let { address, quantity } = commands[cmdIdx];

      for (let i = 0; i < quantity; i++) {
        const regAddr = address + i;

        const found = extendedValues.find(
          (val) =>
            val.element.address <= regAddr &&
            val.element.address + val.element.quantity > regAddr
        );
        if (found) {
          const offset = regAddr - found.element.address;
          registerResponse[regAddr] = found.data.slice(offset * 2, offset * 2 + 2);
        }
      }

      if (options.useValidityMask) {
        const validityWrite = new Array(quantity).fill(true);
        for (let i = 0; i < quantity; i++) {
          const regAddr = address + i;
          const fieldKey = Object.keys(table).find((k) => table[k]?.address === regAddr) as keyof RegisterTable;
          if (!fieldKey) {
            continue;
          }

          validityWrite[i] = this._validity[fieldKey];
        }

        /**
         * If the last register is invalid, we need to reduce the quantity to avoid writing invalid
         * registers. This is because the inverter will not write the registers if the last one is invalid.
         */
        const lastValidIndex = validityWrite.lastIndexOf(true);
        if (lastValidIndex < quantity - 1) {
          quantity = lastValidIndex + 1;
        }
      }

      const data = new Uint8Array(quantity * 2);
      for (let i = 0; i < quantity; i++) {
        const regAddr = address + i;
        const regVal = registerResponse[regAddr];
        if (!regVal) {
          throw new Error(`Invalid or missing value for register ${regAddr}`);
        }
        data[i * 2] = regVal[0];
        data[i * 2 + 1] = regVal[1];
      }

      const writeRepetitionsCount = options.writeRepetitionsCount || 1;
      const concurrentQuery = options.concurrentQuery ?? this.options.concurrentQuery;
      const canBatch = options.verifyAfterWrite && concurrentQuery && concurrentQuery.count >= 2;

      for (let w = 0; w < writeRepetitionsCount; w++) {
        const writeCmd = this.writeHoldingRegisters(address, data, quantity, options.customWriteFn);
        const expectedFuncCode = options.customWriteFn || ModbusFunctionType.WriteMultipleRegisters;

        if (canBatch) {
          const readCmd = this.readHoldingRegisters(address, quantity);
          const combined = concurrentQuery.encodeMultiQuery([writeCmd, readCmd]);
          const rawResponse = await this.schedule.execute(
            () => this.options.executeCmd(combined),
            CommandPriority.High, `write-page-${address.toString(16)}`, new Date(),
          );
          const [writeResp, readResp] = concurrentQuery.decodeMultiQuery(rawResponse, 2);

          this.validateWriteResponse(writeResp, expectedFuncCode);
          this.verifyWrittenValues(readResp, data, address, quantity, options.verifySkipAddresses);

          // Update cache from verify read (fresh post-write values)
          const verifyDataOffset = 3;
          for (let vi = 0; vi < quantity && (verifyDataOffset + vi * 2 + 1) < readResp.length - 2; vi++) {
            this.registerReadCache.set(address + vi,
              new Uint8Array([readResp[verifyDataOffset + vi * 2], readResp[verifyDataOffset + vi * 2 + 1]]),
            );
          }

          // Decode verify-read into content so ModbusLink.data reflects written values
          const verifyDecoded = getHoldingRegistersValueByTable(address, quantity, readResp, filteredTable);
          if (Object.keys(verifyDecoded).length > 0) {
            this.content = { ...this.content, ...verifyDecoded };
          }
        } else {
          const response = await this.schedule.execute(
            () => this.options.executeCmd(writeCmd),
            CommandPriority.High, `write-page-${address.toString(16)}`, new Date(),
          );
          this.validateWriteResponse(response, expectedFuncCode);

          // No verify read — invalidate written addresses
          for (let di = 0; di < quantity; di++) {
            this.registerReadCache.delete(address + di);
          }
        }
      }
      modbusWriteLog.endTimer(pageTimer, { address: `0x${address.toString(16).toUpperCase().padStart(4, '0')}`, quantity, canBatch });
      updateProgress?.(50 + Math.round(((cmdIdx + 1) / commands.length) * 50));
    }

    modbusWriteLog.endTimer(writeOverallTimer, { totalPages: commands.length, totalValues: values.length });
  }

  private validateWriteResponse(response: Uint8Array, expectedFuncCode: number): void {
    if (response[1] !== expectedFuncCode) {
      if (response[1] >= 0x80) {
        const exceptionCode = response.length >= 3 ? response[2] : undefined;
        const mappedCode = exceptionCode !== undefined
          ? (MODBUS_EXCEPTION_CODE_MAP[exceptionCode] ?? ModbusErrorCode.ModbusError)
          : ModbusErrorCode.ModbusError;
        mlLog(this.options.deviceId || '(default)', `Write exception: FC=0x${response[1].toString(16)}, exceptionCode=0x${(exceptionCode ?? 0).toString(16)} -> ${ModbusErrorCode[mappedCode]}`);
        throw new ModbusError(
          `Write exception response FC=0x${response[1].toString(16)} (exceptionCode=0x${(exceptionCode ?? 0).toString(16)})`,
          mappedCode,
          { exceptionCode },
        );
      }
      throw new Error(`Invalid response function code (expected ${expectedFuncCode}, got ${response[1]})`);
    }
  }

  private verifyWrittenValues(readResp: Uint8Array, expectedData: Uint8Array, address: number, quantity: number, skipAddresses?: number[]): void {
    // Read response format: [addr, FC(0x03), byteCount, ...data, CRC_lo, CRC_hi]
    const byteCount = readResp[2];
    const readData = readResp.slice(3, 3 + byteCount);

    const expectedBytes = expectedData.slice(0, quantity * 2);
    const skipSet = skipAddresses ? new Set(skipAddresses) : undefined;

    for (let reg = 0; reg < quantity; reg++) {
      const regAddr = address + reg;

      if (skipSet?.has(regAddr)) {
        continue;
      }

      const byteOffset = reg * 2;
      if (readData[byteOffset] !== expectedBytes[byteOffset] || readData[byteOffset + 1] !== expectedBytes[byteOffset + 1]) {
        const expectedWord = (expectedBytes[byteOffset] << 8) | expectedBytes[byteOffset + 1];
        const actualWord = (readData[byteOffset] << 8) | readData[byteOffset + 1];

        mlLog(this.options.deviceId || '(default)',
          `Write verification failed at register 0x${regAddr.toString(16).toUpperCase().padStart(4, '0')}: expected=0x${expectedWord.toString(16).toUpperCase().padStart(4, '0')}, got=0x${actualWord.toString(16).toUpperCase().padStart(4, '0')}`);
        throw new ModbusError(
          `Write verification failed at register 0x${regAddr.toString(16).toUpperCase().padStart(4, '0')}: expected=0x${expectedWord.toString(16).toUpperCase().padStart(4, '0')}, got=0x${actualWord.toString(16).toUpperCase().padStart(4, '0')}`,
          ModbusErrorCode.WriteVerificationFailed,
          { registerAddress: regAddr, expectedValue: expectedWord, actualValue: actualWord },
        );
      }
    }

    mlLog(this.options.deviceId || '(default)', `Write verification OK for ${quantity} register(s) starting at 0x${address.toString(16).toUpperCase().padStart(4, '0')}${skipSet?.size ? ` (skipped ${skipSet.size} control register(s))` : ''}`);
  }

  validateRegisterTableByMask(validityMaskKey: keyof RegisterTable, table: Partial<RegisterTable>) {
    const validityMask = table[validityMaskKey];
    if (!validityMask) return;

    const startAddress = validityMask.address;
    const quantity = validityMask.quantity;
    const maskValue = BigInt(this.content[validityMaskKey] as number);
    if (!maskValue) return;

    const isBitValid = (mask: bigint, bitIndex: number): boolean =>
      (mask & (BigInt(1) << BigInt(bitIndex))) !== BigInt(0);

    for (const key of Object.keys(table)) {
      const element = table[key];
      if (element && element.address >= startAddress && element.address < startAddress + quantity * 16) {
        const index = element.address - startAddress;
        const validityFlag = isBitValid(maskValue, index);
        if (!validityFlag) {
          this.content[key] = undefined;
          this._validity[key] = false;
        } else {
          this._validity[key] = true;
        }
      }
    }
  }

  buildRegisterValue(_value: string | number, element: InverterRegister): Uint8Array {
    const numberValue = Number(_value);
    const stringValue = String(_value);
    let data: Uint8Array;
    if (element.dataType === DataType.U16 && !isNaN(numberValue)) {
      data = new Uint8Array(2);
      data[0] = (numberValue & 0xff00) >> 8;
      data[1] = numberValue & 0x00ff;
    } else if (element.dataType === DataType.U32 && !isNaN(numberValue)) {
      data = new Uint8Array(4);
      data[0] = (numberValue & 0xff000000) >> 24;
      data[1] = (numberValue & 0x00ff0000) >> 16;
      data[2] = (numberValue & 0x0000ff00) >> 8;
      data[3] = numberValue & 0x000000ff;
    } else if (element.dataType === DataType.I16 && !isNaN(numberValue)) {
      const signedValue = numberValue < 0 ? numberValue + 65536 : numberValue;
      data = new Uint8Array(2);
      data[0] = (signedValue & 0xff00) >> 8;
      data[1] = signedValue & 0x00ff;
    } else if (element.dataType === DataType.I32 && !isNaN(numberValue)) {
      const signedValue = numberValue < 0 ? numberValue + 4294967296 : numberValue;
      data = new Uint8Array(4);
      data[0] = (signedValue & 0xff000000) >> 24;
      data[1] = (signedValue & 0x00ff0000) >> 16;
      data[2] = (signedValue & 0x0000ff00) >> 8;
      data[3] = signedValue & 0x000000ff;
    } else if (element.dataType === DataType.ASCII && typeof stringValue === 'string') {
      data = new TextEncoder().encode(stringValue);
    } else {
      throw new Error(`Unsupported data type ${DataType[element.dataType!] ?? element.dataType} for register "${element.name}"`);
    }
    return data;
  }

  writeHoldingRegistersStandard(startAddress: number, data: Uint8Array): Uint8Array {
    return this.buildCommand(
      ModbusFunctionType.WriteMultipleRegisters,
      startAddress,
      data.length / 2,
      data
    );
  }

  writeHoldingRegisters(
    startAddress: number,
    data: Uint8Array,
    quantityRegister: number,
    customWriteFn?: ModbusFunctionType
  ): Uint8Array {
    return this.buildCommand(
      customWriteFn || ModbusFunctionType.WriteMultipleRegisters,
      startAddress,
      quantityRegister,
      data
    );
  }

  readHoldingRegisters(startAddress: number, quantity: number): Uint8Array {
    return this.buildCommand(ModbusFunctionType.ReadHoldingRegisters, startAddress, quantity);
  }

  readCustomFnRegisters(mType: ModbusFunctionType) {
    return (startAddress: number, quantity: number): Uint8Array =>
      this.buildCommand(mType, startAddress, quantity);
  }

  buildCommand(
    functionType: ModbusFunctionType,
    startAddress: number,
    quantity: number,
    data?: Uint8Array,
    modbusAddress?: number
  ): Uint8Array {
    const address = modbusAddress || this.options.modbusAddress;
    if (!address) {
      throw new Error('Modbus address is not set');
    }

    // FC 0x13 (CustomV2) omits the byteCount field present in standard FC 0x10.
    // Frame: [addr][0x13][startHi][startLo][qtyHi][qtyLo][...data][crcLo][crcHi]
    if (functionType === ModbusFunctionType.CustomV2WriteMultipleRegisters) {
      const command = new Uint8Array(8 + (data ? data.length : 0));
      command[0] = address;
      command[1] = functionType;
      command[2] = (startAddress & 0xff00) >> 8;
      command[3] = startAddress & 0x00ff;
      command[4] = (quantity & 0xff00) >> 8;
      command[5] = quantity & 0x00ff;
      if (data) {
        command.set(data, 6);
      }
      const { lowByte, highByte } = crc16(command.subarray(0, command.length - 2));
      command[command.length - 2] = lowByte;
      command[command.length - 1] = highByte;
      return command;
    }

    const command = new Uint8Array(8 + (data ? data.length + 1 : 0));
    command[0] = address;
    command[1] = functionType;
    command[2] = (startAddress & 0xff00) >> 8;
    command[3] = startAddress & 0x00ff;
    command[4] = (quantity & 0xff00) >> 8;
    command[5] = quantity & 0x00ff;
    if (data) {
      command[6] = data.length;
      command.set(data, 7);
    }
    const { lowByte, highByte } = crc16(command.subarray(0, command.length - 2));
    command[command.length - 2] = lowByte;
    command[command.length - 1] = highByte;
    return command;
  }

  // ─── Result-based API (safe, no throw) ────────────────────────────────────

  /**
   * Like `executeMixedRequests`, but returns `Result<MultiRequest[], DeviceError>`
   * instead of throwing. The caller decides how to handle errors.
   *
   * On success: `ok(requests)` with populated `.results` maps.
   * On transport failure: `err(TransportError)` — channel is dead.
   * On protocol failure: `err(ProtocolError)` — register issue, channel OK.
   */
  async executeMixedRequestsSafe(requests: MultiRequest[]): Promise<DeviceResult<MultiRequest[]>> {
    try {
      const result = await this.executeMixedRequests(requests);
      return ok(result);
    } catch (error) {
      return err(ModbusLink.toDeviceError(error));
    }
  }

  /**
   * Like `executeWriteMultipleHoldingRegisters`, but returns `Result<void, DeviceError>`.
   */
  async executeWriteMultipleHoldingRegistersSafe(
    values: { key: keyof RegisterTable; value: string | number }[],
    table: Partial<RegisterTable>,
    options?: Partial<ModbusCommandOps>,
  ): Promise<DeviceResult<void>> {
    try {
      await this.executeWriteMultipleHoldingRegisters(values, table, options);
      return ok(undefined);
    } catch (error) {
      return err(ModbusLink.toDeviceError(error));
    }
  }

  /**
   * Like `executeRawOperation`, but returns `Result<Uint8Array, DeviceError>`.
   */
  async executeRawOperationSafe(request: Uint8Array): Promise<DeviceResult<Uint8Array>> {
    try {
      const result = await this.executeRawOperation(request);
      return ok(result);
    } catch (error) {
      return err(ModbusLink.toDeviceError(error));
    }
  }

  /**
   * Like `executeBulkRawOperation`, but returns `Result<Uint8Array[], DeviceError>`.
   */
  async executeBulkRawOperationSafe(requests: Uint8Array[]): Promise<DeviceResult<Uint8Array[]>> {
    try {
      const result = await this.executeBulkRawOperation(requests);
      return ok(result);
    } catch (error) {
      return err(ModbusLink.toDeviceError(error));
    }
  }

  /** Access the circuit breaker for this device's scheduler. */
  get circuitBreaker() {
    return this.schedule.circuitBreaker;
  }

  /**
   * Convert a caught error (ModbusError or generic) into a DeviceError.
   * Used by the safe API wrappers.
   */
  static toDeviceError(error: unknown): DeviceError {
    if (error && typeof error === 'object' && 'kind' in error) {
      return error as DeviceError;
    }

    if (error instanceof ModbusError) {
      // Transport-level errors
      if (
        error.code === ModbusErrorCode.RequestFailed ||
        error.code === ModbusErrorCode.Timeout ||
        error.code === ModbusErrorCode.FatalError ||
        error.code === ModbusErrorCode.BusError0500 ||
        error.code === ModbusErrorCode.GatewayPathUnavailable ||
        error.code === ModbusErrorCode.GatewayTargetFailed
      ) {
        const reasonMap: Record<number, DeviceError> = {
          [ModbusErrorCode.RequestFailed]: transportError('request-failed', { message: error.message }),
          [ModbusErrorCode.Timeout]: transportError('timeout', { message: error.message }),
          [ModbusErrorCode.FatalError]: transportError('disconnected', { message: error.message }),
          [ModbusErrorCode.BusError0500]: transportError('disconnected', { message: error.message }),
          [ModbusErrorCode.GatewayPathUnavailable]: transportError('unreachable', { message: error.message }),
          [ModbusErrorCode.GatewayTargetFailed]: transportError('unreachable', { message: error.message }),
        };
        return reasonMap[error.code];
      }

      // Write verification failure
      if (error.code === ModbusErrorCode.WriteVerificationFailed) {
        return verificationError(
          error.registerAddress ?? 0,
          error.expectedValue ?? 0,
          error.actualValue ?? 0,
        );
      }

      // Protocol-level errors (Modbus exceptions)
      if (error.exceptionCode !== undefined) {
        return protocolError(error.exceptionCode, { subResponseIndex: error.subResponseIndex });
      }

      // Mapped Modbus exceptions (0x01-0x04)
      if (error.code <= 0x04 && error.code >= 0x01) {
        return protocolError(error.code);
      }

      // Decode errors (201-207): corrupted or truncated response = transport problem.
      // These indicate the channel delivered garbage, not a valid Modbus exception.
      if (error.code >= 201 && error.code <= 207) {
        return transportError('request-failed', { message: error.message });
      }

      // Generic protocol error
      return protocolError(0xFF, { subResponseIndex: error.subResponseIndex });
    }

    // Unknown error → transport
    return transportError('request-failed', {
      message: error instanceof Error ? error.message : String(error),
    });
  }
}
